import React, { useEffect, useMemo, useState } from 'react';

import { Center, Loader, Alert, useMantineTheme } from '@mantine/core';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, StripeConstructorOptions, loadStripe } from '@stripe/stripe-js';
import { IconExclamationCircle } from '@tabler/icons-react';
import config from 'config';
import { useAuth } from 'context/auth-context';
import useSalesPortalGymPaymentSettings from 'hooks/salesPortal/useSalesPortalGymPaymentSettings';

interface SetupPaymentMethodProviderProps {
  gymId: string;
  children: React.ReactNode;
}

// Wraps a checkout in Stripe Elements configured for the gym's Connect account. The gym's
// payment settings endpoint is authenticated, so the fetch waits for a session: the public sales-portal
// checkout renders before the buyer signs in (the account step is inside the accordion),
// and an anonymous visitor must see the checkout, not a payment-information error. Until
// then Elements mounts with `stripe={null}` — nothing in the pre-login steps needs Stripe.
const SetupPaymentMethodProvider = ({ gymId, children }: SetupPaymentMethodProviderProps): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const isAuthenticated = !!session?.user;
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | undefined>();
  const [state, setState] = useState<'anonymous' | 'loading' | 'error' | 'success'>(
    isAuthenticated ? 'loading' : 'anonymous',
  );

  const {
    data: gymData,
    isLoading: isGymLoading,
    isSuccess: isGymSuccess,
    isError: isGymError,
  } = useSalesPortalGymPaymentSettings(isAuthenticated ? gymId : undefined);

  useEffect(() => {
    if (!isAuthenticated) {
      setState('anonymous');
      setStripePromise(undefined);
      return;
    }

    if (isGymLoading) {
      setState('loading');
      setStripePromise(undefined);
    }

    if (isGymError) {
      setState('error');
      setStripePromise(undefined);
    }

    if (isGymSuccess && gymData) {
      const developerTools = {
        assistant: { enabled: false },
      };

      const stripeOptions: StripeConstructorOptions =
        gymData?.data.stripeAccountType === 'standard'
          ? {
              stripeAccount: gymData?.data.stripeConnectAccount || '',
              developerTools,
            }
          : { developerTools };
      setStripePromise(loadStripe(config.STRIPE_PUBLIC_KEY, stripeOptions));
      setState('success');
    }
  }, [isAuthenticated, gymData, isGymLoading, isGymSuccess, isGymError]);

  const onBehalfOf = useMemo(() => {
    if (!gymData?.data) {
      return undefined;
    }

    if (gymData.data.stripeAccountType === 'custom') {
      return gymData.data.stripeConnectAccount || undefined;
    }

    return undefined;
  }, [gymData]);

  const currency = useMemo(() => {
    return gymData?.data?.defaultCurrency || 'usd';
  }, [gymData]);

  if (state === 'error') {
    return (
      <Center my='xl'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong loading payment information!
        </Alert>
      </Center>
    );
  }

  if (isAuthenticated && (state === 'loading' || !stripePromise || !gymData?.data)) {
    return (
      <Center my='xl'>
        <Loader />
      </Center>
    );
  }

  return (
    <Elements
      stripe={stripePromise ?? null}
      options={{
        mode: 'setup',
        paymentMethodCreation: 'manual',
        currency,
        onBehalfOf,
      }}
    >
      {children}
    </Elements>
  );
};

export default SetupPaymentMethodProvider;
